import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { transformQuillHtml } from '../utils/quillHtmlTransform';

function About() {
  const [pageTitle, setPageTitle] = useState(null);
  const [pageContent, setPageContent] = useState(null);

  const fetchPageContent = useCallback(async () => {
    const result = await contentService.getPageBySlug('about-us');
    if (result.success && result.data) {
      setPageTitle(result.data.title);
      setPageContent(result.data.content);
    }
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[About] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    },
    onPageUpdated: (slug, title) => {
      if (slug === 'about-us') {
        fetchPageContent();
        toast.info(`${title} page has been updated`);
      }
    },
    onPageDeleted: (slug) => {
      if (slug === 'about-us') {
        setPageTitle(null);
        setPageContent(null);
      }
    }
  });

  useEffect(() => {
    // Handle window load event (fade out loader)
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Initialize WOW animations
    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  useEffect(() => {
    if (featureFlags.useCmsContent()) {
      fetchPageContent();
    }
  }, [fetchPageContent]);

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* START ABOUT US CONTENT */}
      <div className="about_content">
        <div className="container">

          {pageContent ? (
            /* API content */
            <>
              <div className="row">
                <div className="col-12 text-center wow slideInUp" data-wow-duration="2s">
                  <h1 className="heading">{pageTitle}</h1>
                </div>
              </div>
              <div className="row mt-5">
                <div className="col-lg-10 offset-lg-1">
                  <div className="cms-content text-left" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(transformQuillHtml(pageContent), { ADD_ATTR: ['style'] }) }} />
                </div>
              </div>
            </>
          ) : (
            /* Hardcoded fallback */
            <>
              {/* Page Header */}
              <div className="row">
                <div className="col-12 text-center wow slideInUp" data-wow-duration="2s">
                  <h1 className="heading">About Us</h1>
                  <p className="para_text mb-2">
                    Turning sporting memories into art is our passion. Making this accessible
                    to the visually impaired is a dream come true.
                  </p>
                </div>
              </div>

              {/* About Us Content */}
              <div className="row mt-5">
                <div className="col-lg-10 offset-lg-1">

                  {/* Section 1: Our Story */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">Our Story</h2>
                    <p className="mb-3">
                      Touchline Memories was born out of a deep love for sport and an unwavering belief
                      that iconic moments deserve to be celebrated as art. From the roar of a stadium
                      to the silence before a penalty kick, we capture the emotion and drama of sport
                      in every piece we create.
                    </p>
                    <p className="mb-3">
                      Founded by fans, for fans — we understand that sport is more than a game.
                      It is history, identity, and shared experience. Our mission is to bring those
                      moments into your home, office, or workspace in a way that inspires every day.
                    </p>
                  </section>

                  {/* Section 2: Our Mission */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">Our Mission</h2>
                    <p className="mb-3">
                      At Touchline Memories, we believe that great art should be accessible to everyone.
                      That is why we are committed to making our sporting art available to the visually
                      impaired community through detailed audio descriptions and accessible product
                      experiences.
                    </p>
                    <p className="mb-3">
                      Every piece in our collection is crafted with care, combining the passion of a
                      sports fan with the eye of an artist. We work with talented artists who share
                      our love of sport to produce prints and artwork that truly capture the spirit
                      of the moment.
                    </p>
                  </section>

                  {/* Section 3: What We Offer */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">What We Offer</h2>
                    <ul className="mb-4">
                      <li>High-quality art prints of iconic sporting moments</li>
                      <li>Coverage across football, basketball, tennis, athletics, and more</li>
                      <li>Curated collections by tournament, athlete, and era</li>
                      <li>Accessible product descriptions for the visually impaired</li>
                      <li>Secure worldwide shipping</li>
                    </ul>
                  </section>

                  {/* Section 4: Get in Touch */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">Get in Touch</h2>
                    <p className="mb-3">
                      We would love to hear from you — whether you have a question about an order,
                      a suggestion for a new collection, or just want to talk sport.
                    </p>
                    <Link to="/contact" className="btn pink-gradient-btn-into-transparent">
                      Contact Us
                    </Link>
                  </section>

                </div>
              </div>
            </>
          )}

        </div>
      </div>
      {/* END ABOUT US CONTENT */}
    </Layout>
  );
}

export default About;
